import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Cake, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import Logo from '../components/Logo';
import AppleLoginButton from '../components/AppleLoginButton';
import api from '../lib/api';
import { toast } from 'sonner';

// Small icon proxy so JSX below stays readable.
function ShieldIcon() { return <ShieldCheck size={13} className="text-emerald-300" />; }

// Max birthdate = today (defensive HTML validation — server also enforces).
const TODAY_ISO = new Date().toISOString().slice(0, 10);

export default function Signup() {
  const { register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ displayName: '', email: '', password: '', date_of_birth: '' });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    if (!form.date_of_birth) {
      setError('Please enter your birthdate. WorldVibe is for travelers 13 and above.');
      return;
    }
    setBusy(true); setError('');
    const res = await register(
      form.email.trim(),
      form.password,
      form.displayName.trim(),
      form.date_of_birth,
      { showOnFounderWall: false },
    );
    setBusy(false);
    if (res.ok) { toast.success('Welcome to WorldVibe ✨'); api.post('/planets/p5/join').catch(() => {}); nav('/app/planets/p5'); }
    else setError(res.error);
  };

  return (
    <div className="min-h-screen cosmic-bg relative flex items-center justify-center px-4 py-10">
      <div className="stars-bg" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <Link to="/" aria-label="Back to WorldVibe home" data-testid="signup-home-link" className="hover:opacity-90 transition">
            <Logo size="lg" showText animate />
          </Link>
        </div>
        <div className="glass rounded-3xl p-7 cosmic-border">
          <h1 className="font-display font-bold text-2xl">Create your traveler</h1>
          <p className="text-white/60 text-sm mt-1">Meet the World in Real Time.</p>

          <Button onClick={loginWithGoogle} variant="outline" className="w-full mt-6 bg-white text-black hover:bg-white/90 border-0 font-semibold">
            <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83Z"/><path fill="#EA4335" d="M12 5.38c1.61 0 3.06.55 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Sign up with Google
          </Button>
          <AppleLoginButton label="Sign up with Apple" next="/app/planets/p5" />

          <div className="flex items-center gap-3 my-5 text-xs text-white/40">
            <div className="flex-1 h-px bg-white/10" /> or with email <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} required minLength={2} placeholder="Display name" className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
            </div>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" required placeholder="Email" className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} type="password" minLength={6} required placeholder="Password (min 6 chars)" className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
            </div>
            <div className="relative">
              <Cake size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={form.date_of_birth}
                onChange={e => setForm({ ...form, date_of_birth: e.target.value })}
                type="date"
                required
                max={TODAY_ISO}
                data-testid="signup-dob-input"
                className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <div className="mt-1 pl-1 text-[10px] text-white/40 leading-snug">
                Must be 13 or older to join WorldVibe. <span className="text-amber-200/80">Dating features unlock at 18+.</span> Everything else — chat, planets, games — is open to teens.
              </div>
            </div>
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 text-[11px] text-white/60 leading-relaxed" data-testid="signup-ugc-notice">
            <div className="font-semibold text-white/85 mb-1 flex items-center gap-1.5">
              <ShieldIcon /> Community safety at WorldVibe
            </div>
            Zero tolerance for harassment, hate speech, nudity, or content that objectifies real people. Every profile, message, and photo passes automated moderation. Use the <span className="text-cyan-300">Flag</span> icon on any card to report — moderators respond within 24 hours and abusive accounts are removed.
          </div>
          {error && <div className="text-sm text-rose-400">{error}</div>}
            <label className="flex items-start gap-2.5 text-xs text-white/65 cursor-pointer select-none pt-1">
              <Checkbox
                checked={accepted}
                onCheckedChange={(v) => setAccepted(Boolean(v))}
                data-testid="signup-tos-checkbox"
                className="mt-0.5 border-white/30 data-[state=checked]:bg-fuchsia-500 data-[state=checked]:border-fuchsia-500"
              />
              <span className="leading-relaxed">
                I&apos;m at least 18 years old, agree to the{' '}
                <Link to="/terms" target="_blank" className="text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline">Privacy Policy</Link>
                , and understand there is <strong className="text-white/85">zero tolerance</strong> for objectionable content or abusive users per our{' '}
                <Link to="/guidelines" target="_blank" className="text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline">Community Guidelines</Link>.
              </span>
            </label>

            <Button type="submit" disabled={busy || !accepted} data-testid="signup-submit-btn" className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white border-0 font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <ArrowRight className="mr-2" size={16} />} Create account
            </Button>
          </form>
          <div className="text-center text-sm text-white/60 mt-5">Already a traveler? <Link to="/login" className="text-cyan-300 hover:text-cyan-200">Sign in</Link></div>
        </div>
      </div>
    </div>
  );
}
